import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { switchOrganization } from '@/modules/auth/services/auth.service';
import { useAuthStore } from '@/state/store/auth';
import { useToast } from '@/hooks/use-toast';
import { HttpError } from '@/lib/https';
import { useGetMultiOrgs } from '@/lib/api/hooks/use-multi-orgs';
import type { Organization } from '@/lib/api/types/multi-orgs.types';

const isOrganizationEnabled = (org: Organization) => org.isEnable !== false;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * Organization id -> the roles held there, read from whichever shape the account record actually has.
 * `account.memberships` (the SDK-typed shape) is tried first, but a live user record was confirmed to
 * carry `organizationIds` + `OrganizationsRoles` (an `{orgId: roles[]}` map) instead — same mismatch
 * already found and fixed for `useOrganizations` — so both are read defensively here too.
 */
const organizationRolesById = (account: unknown): Record<string, string[]> => {
  const record = asRecord(account);
  const rolesByOrgId: Record<string, string[]> = {};

  (record.memberships as { organizationId?: string; roles?: string[] }[] | undefined)?.forEach(
    (membership) => {
      if (membership.organizationId)
        rolesByOrgId[membership.organizationId] = membership.roles ?? [];
    }
  );

  const organizationsRoles = asRecord(record.OrganizationsRoles);
  Object.keys(organizationsRoles).forEach((organizationId) => {
    rolesByOrgId[organizationId] = asStringArray(organizationsRoles[organizationId]);
  });

  const flatRoles = asStringArray(record.roles);
  asStringArray(record.organizationIds).forEach((organizationId) => {
    if (!(organizationId in rolesByOrgId)) rolesByOrgId[organizationId] = flatRoles;
  });
  if (typeof record.organizationId === 'string' && record.organizationId) {
    if (!(record.organizationId in rolesByOrgId)) rolesByOrgId[record.organizationId] = flatRoles;
  }

  return rolesByOrgId;
};

export const useOrganizationSwitcher = () => {
  const [isSwitching, setIsSwitching] = useState(false);
  const { t } = useTranslation();
  const { setTokens, selectedOrgId } = useAuthStore();
  const { toast } = useToast();

  const {
    data: account,
    isLoading: isLoadingAccount,
    isSuccess: isAccountLoaded,
  } = useGetAccount();
  const { data: orgsData, isLoading: isLoadingOrgs } = useGetMultiOrgs({
    enabled: isAccountLoaded && !!account,
  });

  // From `/iam/me`, which reflects the access token's claims: an organization granted since the token was
  // issued shows once it is refreshed. Not `users.get(myItemId)`: that needs `blocks-iam::iam::users`
  // (read every user), which regular employees don't hold, and this runs on every page.
  const rolesByOrgId = useMemo(() => organizationRolesById(account), [account]);

  // `/iam/organizations/my` already lists only the user's own organizations. Not narrowed to the ones on
  // `/iam/me`: those reflect the access token's claims and can leave out organizations the user belongs to.
  const availableOrganizations = useMemo(
    () => (orgsData?.organizations ?? []).filter(isOrganizationEnabled),
    [orgsData?.organizations]
  );

  // Only the organization the session is really in is marked; nothing is guessed when it is not listed.
  const selectedOrganization = useMemo(
    () => availableOrganizations.find((org) => org.itemId === selectedOrgId) ?? null,
    [availableOrganizations, selectedOrgId]
  );

  const currentOrgRoles = useMemo(() => {
    if (!selectedOrgId) return [];
    return rolesByOrgId[selectedOrgId] ?? [];
  }, [rolesByOrgId, selectedOrgId]);

  const handleOrganizationSelect = async (orgId: string, onSuccess?: () => void) => {
    if (isSwitching || orgId === selectedOrganization?.itemId) {
      return;
    }

    try {
      setIsSwitching(true);

      const response = await switchOrganization(orgId);

      setTokens({
        accessToken: response.access_token,
        refreshToken: (response.refresh_token || useAuthStore.getState().refreshToken) ?? '',
      });

      onSuccess?.();
      window.location.reload();
      return;
    } catch (error) {
      let errorTitle = t('FAILED_TO_SWITCH_ORGANIZATION');
      let errorDescription = t('SOMETHING_WENT_WRONG');

      if (error instanceof HttpError) {
        const errorData = error.error;

        if (errorData?.error === 'user_inactive_or_not_verified') {
          errorTitle = t('ACCESS_DENIED');
          errorDescription =
            typeof errorData?.error_description === 'string'
              ? errorData.error_description
              : t('USER_NOT_EXIST_IN_ORGANIZATION');
        } else if (typeof errorData?.error_description === 'string') {
          errorDescription = errorData.error_description;
        } else if (typeof errorData?.error === 'string') {
          errorDescription = errorData.error;
        }
      }

      toast({
        variant: 'destructive',
        title: errorTitle,
        description: errorDescription,
      });
    } finally {
      setIsSwitching(false);
    }
  };

  return {
    account,
    availableOrganizations,
    currentOrgRoles,
    handleOrganizationSelect,
    isLoading: isLoadingAccount || isLoadingOrgs,
    isSwitching,
    rolesByOrgId,
    selectedOrgId,
    selectedOrganization,
  };
};
